import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, Select, Button } from 'antd';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Box } from 'atoms';
import { searchDriversV2 } from 'store/driverSliceV2';
import { replaceTripDriverV2 } from 'store/tripSliceV2';

const { Option } = Select;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}

	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 0;
	}

	.ant-modal-title {
		font-size: 20px;
		font-weight: 600;
		color: #1f2937;
	}

	.ant-modal-body {
		padding: 24px;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
		font-size: 14px;
	}

	.ant-input,
	.ant-select-selector {
		height: 40px;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		background-color: #fff;
	}

	.ant-input:disabled,
	.ant-select-disabled .ant-select-selector {
		background-color: #e5e7eb;
		color: #6b7280;
	}

	.ant-select-selector {
		display: flex;
		align-items: center;
	}

	.ant-input:hover,
	.ant-select-selector:hover {
		border-color: #9ca3af;
	}

	.ant-input:focus,
	.ant-select-focused .ant-select-selector {
		border-color: #1e3a8a;
		box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
	}
`;

const FormGrid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
`;

const SectionDivider = styled.div`
	border-top: 1px solid #e5e7eb;
	margin: 4px 0 20px;
`;

const ModalFooter = styled(Box)`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		border-color: #9ca3af !important;
		color: #111827 !important;
		background: white !important;
	}
`;

const ConfirmButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
		color: white !important;
	}

	&:disabled {
		background-color: #9ca3af !important;
		border-color: #9ca3af !important;
		cursor: not-allowed;
	}
`;

function ReplaceDriverModal({ visible, tripData, onConfirm, onClose }) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [driverOptions, setDriverOptions] = useState([]);

	useEffect(() => {
		if (visible && tripData) {
			form.setFieldsValue({
				vendor_name: tripData.vendor_name,
				vehicle_type: tripData.vehicle_type,
				driver_name: tripData.driver_name,
				new_driver_id: undefined,
				reason: '',
			});
		}
	}, [visible, tripData, form]);
	useEffect(() => {
		if (visible && tripData?.vendor_id && tripData?.vehicle_type_id) {
			dispatch(
				searchDriversV2({
					filter_by: {
						vendor_id: tripData.vendor_id,
						vehicle_type_ids: [Number(tripData.vehicle_type_id)],
						is_active: true,
					},
					offset: 0,
					limit: 1000,
				})
			).then((res) => {
				const options = (res?.rows || [])
					.filter(
						(d) => !tripData.driver_id || Number(d.id) !== Number(tripData.driver_id)
					)
					.map((d) => ({
						value: d.id,
						label: d.driver_name || d.name || '-',
						item: d,
					}));
				setDriverOptions(options);
			});
		}
	}, [visible, tripData, dispatch]);

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	const handleConfirm = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const selectedDriver = driverOptions.find((d) => d.value === values.new_driver_id);

			const response = await dispatch(
				replaceTripDriverV2({
					trip_id: tripData?.trip_id,
					new_driver_id: values.new_driver_id,
					reason: values.reason,
				})
			);

			if (response?.status !== 'success') {
				return;
			}

			const payload = {
				trip_id: tripData?.trip_id,
				replacement_id: response?.data?.replacement_id,
				old_driver_id: response?.data?.old_driver_id ?? tripData?.driver_id,
				old_driver_name: tripData?.driver_name,
				new_driver_id: response?.data?.new_driver_id ?? values.new_driver_id,
				new_driver_name: selectedDriver?.label,
				new_driver_number:
					selectedDriver?.item?.mobile_number ||
					selectedDriver?.item?.driver_number ||
					'',
				reason: values.reason,
			};

			const success = await onConfirm(payload);
			if (success) {
				form.resetFields();
			}
		} catch (error) {
			console.error('Validation error:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<StyledModal
			title="Replace Driver"
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={700}
			destroyOnClose
		>
			<StyledForm form={form} layout="vertical">
				<FormGrid>
					<Form.Item name="vendor_name" label="Vendor">
						<Input disabled />
					</Form.Item>
					<Form.Item name="vehicle_type" label="Vehicle Type">
						<Input disabled />
					</Form.Item>
				</FormGrid>

				<Form.Item name="driver_name" label="Driver Name">
					<Input disabled style={{ maxWidth: 'calc(50% - 10px)' }} />
				</Form.Item>

				<FormGrid>
					<Form.Item
						name="new_driver_id"
						label="Replace Driver with"
						rules={[{ required: true, message: 'Please select a driver' }]}
					>
						<Select placeholder="Select Driver" showSearch optionFilterProp="children">
							{driverOptions.map((d) => (
								<Option key={d.value} value={d.value}>
									{d.label}
								</Option>
							))}
						</Select>
					</Form.Item>
					<Form.Item
						name="reason"
						label="Reason for Replacement"
						rules={[{ required: true, message: 'Please enter reason' }]}
					>
						<Input placeholder="Enter Reason" />
					</Form.Item>
				</FormGrid>

				<SectionDivider />

				<ModalFooter>
					<CancelButton onClick={handleCancel}>Cancel</CancelButton>
					<ConfirmButton onClick={handleConfirm} loading={loading}>
						Confirm
					</ConfirmButton>
				</ModalFooter>
			</StyledForm>
		</StyledModal>
	);
}

ReplaceDriverModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	tripData: PropTypes.shape({
		trip_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_name: PropTypes.string,
		vehicle_type: PropTypes.string,
		vehicle_type_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		driver_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		driver_name: PropTypes.string,
	}),
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default ReplaceDriverModal;
